import React, { useState } from 'react';
import { MCQ } from '../types/mcq';
import { Button } from './ui/Button';
import { Search, ArrowRight, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { MCQView } from './MCQView';
import { MCQEdit } from './MCQEdit';
import { MCQMatchCard } from './mcq/MCQMatchCard';

interface CurrentMCQProps {
  mcq: MCQ | null;
  potentialMatches: MCQ[];
  onFindMatches: () => void;
  onNextQuestion: () => void;
  onConfirmDuplicate: (duplicateId: number) => void;
  onMarkCurrentAsDuplicate: () => void;
  onUpdateMCQ: (updatedMCQ: MCQ) => void;
  loading: boolean;
  matchesLoading: boolean;
}

export function CurrentMCQ({ 
  mcq,
  potentialMatches,
  onFindMatches,
  onNextQuestion,
  onConfirmDuplicate,
  onMarkCurrentAsDuplicate,
  onUpdateMCQ,
  loading,
  matchesLoading
}: CurrentMCQProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [expandedMCQ, setExpandedMCQ] = useState<number | null>(null);
  const [editingMatchId, setEditingMatchId] = useState<number | null>(null);

  // Sort matches by similarity score in descending order
  const sortedMatches = [...potentialMatches].sort((a, b) => 
    (b.similarityScore || 0) - (a.similarityScore || 0)
  );

  if (loading) {
    return (
      <div className="animate-pulse p-4 sm:p-6 bg-white rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (!mcq) {
    return (
      <div className="p-4 sm:p-6 bg-white rounded-lg shadow text-center">
        <p className="text-gray-500">No MCQs available for review</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Current Question</h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                onClick={onFindMatches} 
                disabled={matchesLoading}
                className="w-full sm:w-auto px-4 sm:px-6"
              >
                {matchesLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Finding Matches...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Find Matches
                  </>
                )}
              </Button>
              <Button 
                onClick={onNextQuestion}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Next Question
              </Button>
            </div>
          </div>

          {isEditing ? (
            <MCQEdit
              mcq={mcq}
              onSave={(updatedMCQ) => {
                onUpdateMCQ(updatedMCQ);
                setIsEditing(false);
              }}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <MCQView
              mcq={mcq}
              onEdit={() => setIsEditing(true)}
              onMarkAsDuplicate={onMarkCurrentAsDuplicate}
              onUpdateMCQ={onUpdateMCQ}
            />
          )}
        </div>
      </div>

      {sortedMatches.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Potential Matches ({sortedMatches.length})
          </h3>
          
          {sortedMatches.map((match) => (
            <MCQMatchCard
              key={match.id}
              match={match}
              isExpanded={expandedMCQ === match.id}
              isEditing={editingMatchId === match.id}
              onToggleExpand={(id) => setExpandedMCQ(expandedMCQ === id ? null : id)}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1 mr-4">
                  {expandedMCQ !== match.id && (
                    <>
                      <p className="text-gray-900 line-clamp-2">{match.question}</p>
                      <p className="text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded-md inline-block">
                        {match.answer}. {match[`option${match.answer}`]}
                      </p>
                    </>
                  )}
                </div>
                {expandedMCQ === match.id ? (
                  <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </div>

              {expandedMCQ === match.id && (
                <div className="mt-6" onClick={(e) => e.stopPropagation()}>
                  {editingMatchId === match.id ? (
                    <MCQEdit
                      mcq={match}
                      onSave={(updatedMCQ) => {
                        onUpdateMCQ(updatedMCQ);
                        setEditingMatchId(null);
                      }}
                      onCancel={() => setEditingMatchId(null)}
                    />
                  ) : (
                    <MCQView
                      mcq={match}
                      onEdit={() => setEditingMatchId(match.id)}
                      onMarkAsDuplicate={() => onConfirmDuplicate(match.id)}
                      onUpdateMCQ={onUpdateMCQ}
                    />
                  )}
                </div>
              )}

              {!expandedMCQ && (
                <div className="flex justify-end mt-4">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onConfirmDuplicate(match.id);
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6"
                  >
                    Mark as Duplicate
                  </Button>
                </div>
              )}
            </MCQMatchCard>
          ))}
        </div>
      )}
    </div>
  );
}